<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    {{formData.dateWhenItWasSentOut}}
    <div class="row mb-4">
      <div class="col-12">
        Der erste Termin muss zwischen
        {{ coachingStartDate }} und
        {{ coachingEndDate }} liegen
      </div>
    </div>
    <div class="row">
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <Datepicker
            v-model="formData.termin"
            :required="true"
            label="Termin"
            position="down"
            :validation="v$.termin"
            :messages="v$.termin.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-4 col-sm-12">
        <Timepicker
            v-model="formData.startTime"
            :required="true"
            :minutesArray="minutesArray"
            label="Startzeit"/>
      </div>
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <Datepicker
            v-model="formData.dateWhenItWasSentOut"
            label="Dokument versendet am:"
            position="down"/>
      </div>
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <BaseInput
            v-model="formData.dateWhenInvitationIsCreated"
            label="Dokument erstellt am:"
            :disabled="true"/>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 col-md-12 col-sm-12">
        <SelectVue v-model="formData.coachId"
                   :options="coachDropdown"
                   :required="true"
                   label="Coach"
                   placeholder="Trainer auswählen"
                   :validation="v$.coachId"
                   :messages="v$.coachId.required.$message"/>
      </div>
    </div>

    <div class="d-flex justify-content-center w-100 mt-3">
      <button class="btn btn-primary" type="submit">Einladungsschreiben erstellen</button>
    </div>
  </form>
</template>

<script setup>
import dropdownService from '@/api/Dropdown';
import portalAssistedLivingCoachingService from '@/api/Portal/AssistedLivingCoaching';
import BaseInput from '@/components/base/BaseInput.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import Timepicker from '@/components/picker/Timepicker.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { addDeletedCoachToDropdown } from '@/shared/portal/commonFunctions';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, defineEmits, defineProps, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }         = useI18n();
const isLoaded      = ref(false);
const isSubmitting  = ref(false);
const coachDropdown = ref([]);
const emit          = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  coachingStartDate: {
    type: String,
    required: true,
  },
  coachingEndDate: {
    type: String,
    required: true,
  },
  coachingId: {
    type: Number,
    required: true,
  },
  invitationId: {
    type: Number,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const coachingId        = toRefs(props)[ 'coachingId' ];
const coachingStartDate = toRefs(props)[ 'coachingStartDate' ];
const coachingEndDate   = toRefs(props)[ 'coachingEndDate' ];
const invitationId      = toRefs(props)[ 'invitationId' ];

const formData = ref({
  coachingId: '',
  termin: '',
  startTime: '00:00',
  dateWhenItWasSentOut: '',
  coachId: null,
  dateWhenInvitationIsCreated: '',
});

const formRules = ref({
  termin: requiredRule(t),
  startTime: requiredRule(t),
  coachId: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$            = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.coachingId = +coachingId.value;

    if (props.invitationId) {
      await portalAssistedLivingCoachingService.putInvitation(formData.value, +invitationId.value);
    } else {
      await portalAssistedLivingCoachingService.postInvitation(formData.value);
    }

    SuccessToast(t('general.toasts.save_success'));
    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getInvitationData(invitationId) {
  try {
    const { data }                    = await portalAssistedLivingCoachingService.getInvitation(+invitationId);
    const coachId                     = data.data?.coach?.id;
    const dateWhenInvitationIsCreated = data.data.createdAt;

    addDeletedCoachToDropdown(data.data?.coach, coachDropdown);

    formData.value = {
      ...data.data,
      dateWhenInvitationIsCreated: dateWhenInvitationIsCreated,
      coachId: coachId,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getAssignedCoaches(coachingId) {
  try {
    CloseToast();
    const { data }      = await dropdownService.getAssistedLivingCoachingAssignedCoaches(+coachingId);
    coachDropdown.value = data.data.map(item => ({
      label: `${item.firstName}` + ' ' + `${item.lastName}`,
      value: item?.id,
    }));
  } catch (error) {
    await ErrorToast(error);
  }
}

const minutesArray = [
  "00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11",
  "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23",
  "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35",
  "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47",
  "48", "49", "50", "51", "52", "53", "54", "55", "56", "57", "58", "59"
];


onMounted(async () => {
  await getAssignedCoaches(+coachingId.value);
  if (invitationId.value) {
    await getInvitationData(+invitationId.value);
  }

  isLoaded.value = true;
});
</script>
<style scoped>

</style>